import { FormEvent, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import WebApp from '@twa-dev/sdk'
import { apiClient } from '../api/client'
import { DetailLoadingState } from '../components/StateComponents'
import { getTelegramUserId, getTelegramUsername } from '../utils/telegram'
import { getAnalyticsContext, trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'

interface TimelineEvent {
  date?: string
  title?: string
  what_happened?: string
}

interface TimelineResponse {
  event_id: string
  timeline_title?: string
  timeline_summary?: string
  events: TimelineEvent[]
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatResponse {
  success: boolean
  reply?: string
  error?: string
}

export function TimelineChatPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const date = searchParams.get('date') || 'latest'
  const chatId = getTelegramUserId()
  const username = getTelegramUsername()
  const { lang, t } = useLanguage()

  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)

  const { data: timeline, isLoading: timelineLoading, error: timelineError } = useQuery({
    queryKey: ['timeline', id, date, lang],
    queryFn: () => apiClient.get<TimelineResponse>(`/api/events/${id}/timeline`, { date, lang }),
    enabled: !!id,
    retry: false,
  })

  const { isLoading: sessionLoading } = useQuery({
    queryKey: ['timeline-chat-session', id, date, chatId],
    queryFn: () => apiClient.post(`/api/interpret/${id}/simple?date=${date}`, {
      user_id: chatId,
      username,
      charge: false,
      analytics_context: getAnalyticsContext(),
    }),
    enabled: !!id,
    staleTime: 0,
  })

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const question = input.trim()
    if (!question || !id || isSending || sessionLoading) return

    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: question }])
    setIsSending(true)
    const startedAt = performance.now()
    trackBusinessEvent('timeline_chat_send', {
      event_id: id,
      message_text: question,
      message_length: question.length,
      turn_index: messages.length + 1,
    }, id)

    try {
      const response = await apiClient.post<ChatResponse>(`/api/chat/${id}`, {
        chat_id: chatId,
        message: question,
        analytics_context: getAnalyticsContext(),
      })

      if (response.success && response.reply) {
        setMessages((prev) => [...prev, { role: 'assistant', content: response.reply! }])
        WebApp.HapticFeedback.notificationOccurred('success')
        trackBusinessEvent('timeline_chat_success', {
          event_id: id,
          message_length: question.length,
          reply_length: response.reply.length,
          latency_ms: Math.round(performance.now() - startedAt),
        }, id)
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: response.error || t('追问失败，请稍后重试。', 'Follow-up failed. Please try again later.') }])
      }
    } catch (error) {
      console.error('Timeline chat failed:', error)
      trackBusinessEvent('timeline_chat_fail', {
        event_id: id,
        message_length: question.length,
        latency_ms: Math.round(performance.now() - startedAt),
        error: error instanceof Error ? error.message : String(error),
      }, id)
      setMessages((prev) => [...prev, { role: 'assistant', content: t('追问失败，请稍后重试。', 'Follow-up failed. Please try again later.') }])
    } finally {
      setIsSending(false)
    }
  }

  if (timelineLoading) {
    return <DetailLoadingState />
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-24">
      <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ←
          </button>
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">{t('查看时间轴', 'View Timeline')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{date}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          {timelineError || !timeline ? (
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{t('暂无时间轴数据', 'No timeline data')}</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t(
                  'E1 当前没有找到这个事件对应的历史时间轴，但你仍然可以在下面继续追问。',
                  'E1 did not find a historical timeline for this event, but you can still ask follow-up questions below.'
                )}
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('🗓 时间轴：', '🗓 Timeline: ')}{timeline.timeline_title || t('未知时间轴', 'Unknown timeline')}
              </h2>

              {timeline.timeline_summary && (
                <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                  {timeline.timeline_summary}
                </p>
              )}

              <div className="mt-4 space-y-3">
                {timeline.events.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('暂无时间轴事件数据。', 'No timeline events yet.')}</p>
                )}

                {[...timeline.events]
                  .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')))
                  .map((event, index) => (
                    <div key={`${event.date}-${index}`} className="border-l-2 border-blue-200 dark:border-blue-800 pl-3">
                      <div className="text-xs font-medium text-blue-600 dark:text-blue-300">
                        {event.date || t('未知日期', 'Unknown date')}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                        {event.title || t('未知事件', 'Unknown event')}
                      </div>
                      {event.what_happened && (
                        <p className="mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                          {event.what_happened.length > 200
                            ? `${event.what_happened.slice(0, 200)}...`
                            : event.what_happened}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </>
          )}
        </section>

        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('继续追问', 'Ask Follow-Up')}</h2>
          <div className="space-y-3">
            {messages.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(
                  '可以继续问：这条时间轴说明了什么？历史上类似事件如何演化？下一步应该关注哪些信号？',
                  'You can ask: What does this timeline suggest? How did similar events evolve historically? What signals should I watch next?'
                )}
              </p>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  message.role === 'user'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {message.content}
              </div>
            ))}

            {isSending && (
              <div className="rounded-lg p-3 text-sm bg-gray-50 dark:bg-gray-700 text-gray-500">
                {t('正在思考...', 'Thinking...')}
              </div>
            )}
          </div>
        </section>
      </main>

      <form
        onSubmit={handleSubmit}
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-3 z-10"
      >
        <div className="max-w-3xl mx-auto flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sessionLoading || isSending}
            placeholder={sessionLoading ? t('正在准备对话...', 'Preparing chat...') : t('继续追问时间轴...', 'Ask about the timeline...')}
            className="flex-1 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!input.trim() || sessionLoading || isSending}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {t('发送', 'Send')}
          </button>
        </div>
      </form>
    </div>
  )
}
